import {
  Alert,
  Badge,
  Box,
  Button,
  Grid,
  Group,
  Modal,
  Paper,
  SegmentedControl,
  Skeleton,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import React from "react";
import { FaExclamationTriangle, FaSearch, FaSync, FaTrash } from "react-icons/fa";
import { useLanguage } from "../contexts/language.context";
import {
  DELETE_WHITELABEL_RETELL_KNOWLEDGE_BASE,
  GET_WHITELABEL_RETELL_KNOWLEDGE_BASES,
} from "../services/knowledges";
import moment from "moment";

type KnowledgeBaseStatus =
  | "in_progress"
  | "complete"
  | "error"
  | "refreshing_in_progress"
  | string;

interface KnowledgeBaseCompany {
  id: string;
  name: string;
  status: number;
  next_payment: string | null;
  is_active: boolean;
}

interface KnowledgeBaseCategory {
  id: string;
  title: string;
  active: boolean;
}

interface RetellKnowledgeBaseRow {
  knowledge_base_id: string;
  knowledge_base_name: string;
  status: KnowledgeBaseStatus;
  in_database: boolean;
  category: KnowledgeBaseCategory | null;
  company: KnowledgeBaseCompany | null;
}

interface RetellKnowledgeBasesResponse {
  knowledge_bases: RetellKnowledgeBaseRow[];
  total: number;
  orphans: number;
}

type FilterKey = "all" | "orphans" | "linked";

const statusColorMap: Record<string, string> = {
  complete: "green",
  in_progress: "blue",
  refreshing_in_progress: "cyan",
  error: "red",
};

export default function WLRetellKnowledgeBasesAudit() {
  const { str } = useLanguage();

  const [rows, setRows] = React.useState<RetellKnowledgeBaseRow[]>([]);
  const [total, setTotal] = React.useState(0);
  const [orphans, setOrphans] = React.useState(0);
  const [search, setSearch] = React.useState("");
  const [filter, setFilter] = React.useState<FilterKey>("all");
  const [loading, setLoading] = React.useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [deletingId, setDeletingId] = React.useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = React.useState<RetellKnowledgeBaseRow | null>(null);

  const loadData = React.useCallback(async () => {
    setLoading(true);
    setErrorMessage(null);

    try {
      const response: RetellKnowledgeBasesResponse = await GET_WHITELABEL_RETELL_KNOWLEDGE_BASES();
      setRows(response?.knowledge_bases ?? []);
      setTotal(response?.total ?? 0);
      setOrphans(response?.orphans ?? 0);
    } catch (err: any) {
      setErrorMessage(err?.message ?? str("RETELL_KB_AUDIT.LOAD_ERROR"));
    } finally {
      setLoading(false);
      setHasLoadedOnce(true);
    }
  }, [str]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredRows = React.useMemo(() => {
    const term = search.trim().toLowerCase();

    return rows.filter((row) => {
      const isOrphan = !row.in_database;
      const matchesFilter =
        filter === "all" ||
        (filter === "orphans" && isOrphan) ||
        (filter === "linked" && !isOrphan);

      if (!matchesFilter) {
        return false;
      }

      if (!term) {
        return true;
      }

      return (
        row.knowledge_base_name?.toLowerCase().includes(term) ||
        row.knowledge_base_id?.toLowerCase().includes(term)
      );
    });
  }, [filter, rows, search]);

  const linked = Math.max(total - orphans, 0);

  const formatDate = (value: string | null | undefined) => {
    if (!value) {
      return "-";
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "-";
    }

    return moment(date).format(str("DATE_FORMAT"));
  };

  const getCompanyActiveBadge = (row: RetellKnowledgeBaseRow) => {
    if (!row.company) {
      return <Badge color="gray" variant="light">{str("RETELL_KB_AUDIT.UNKNOWN")}</Badge>;
    }

    return row.company.is_active
      ? <Badge color="green" variant="light">{str("RETELL_KB_AUDIT.ACTIVE")}</Badge>
      : <Badge color="red" variant="light">{str("RETELL_KB_AUDIT.INACTIVE")}</Badge>;
  };

  const handleDelete = async () => {
    if (!confirmDelete?.knowledge_base_id) {
      return;
    }

    try {
      setDeletingId(confirmDelete.knowledge_base_id);
      await DELETE_WHITELABEL_RETELL_KNOWLEDGE_BASE(confirmDelete.knowledge_base_id);

      notifications.show({
        message: str("RETELL_KB_AUDIT.DELETE_SUCCESS"),
        color: "green",
      });

      setRows((prev) => prev.filter((item) => item.knowledge_base_id !== confirmDelete.knowledge_base_id));
      setTotal((prev) => Math.max(prev - 1, 0));
      if (!confirmDelete.in_database) {
        setOrphans((prev) => Math.max(prev - 1, 0));
      }

      setConfirmDelete(null);
    } catch (err: any) {
      notifications.show({
        message: err?.message || str("RETELL_KB_AUDIT.DELETE_ERROR"),
        color: "red",
      });
    } finally {
      setDeletingId(null);
    }
  };

  if (loading && !hasLoadedOnce) {
    return (
      <Paper shadow="xs" p="md">
        <Stack>
          <Skeleton height={36} radius="md" />
          <Grid>
            <Grid.Col span={{ base: 12, md: 4 }}><Skeleton height={90} radius="md" /></Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}><Skeleton height={90} radius="md" /></Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}><Skeleton height={90} radius="md" /></Grid.Col>
          </Grid>
          <Skeleton height={42} radius="md" />
          <Skeleton height={320} radius="md" />
        </Stack>
      </Paper>
    );
  }

  return (
    <Box>
      <Modal
        opened={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title={str("RETELL_KB_AUDIT.CONFIRM_DELETE_TITLE")}
        centered
      >
        <Stack>
          <Alert color="red" icon={<FaExclamationTriangle />}>
            {str("RETELL_KB_AUDIT.CONFIRM_DELETE_WARNING")}
          </Alert>
          <Text size="sm">
            {str("RETELL_KB_AUDIT.KB_ID_LABEL")} <b>{confirmDelete?.knowledge_base_id}</b>
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmDelete(null)}>
              {str("CANCEL")}
            </Button>
            <Button
              color="red"
              leftSection={<FaTrash />}
              onClick={handleDelete}
              loading={deletingId === confirmDelete?.knowledge_base_id}
            >
              {str("RETELL_KB_AUDIT.DELETE_IN_RETELL")}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Paper shadow="xs" p="md" mb="md">
        <Group justify="space-between" align="center">
          <Title order={3}>{str("RETELL_KB_AUDIT.TITLE")}</Title>
          <Button
            variant="light"
            leftSection={<FaSync />}
            onClick={loadData}
            loading={loading}
          >
            {str("RETELL_KB_AUDIT.REFRESH")}
          </Button>
        </Group>
      </Paper>

      <Grid mb="md">
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Paper p="md" withBorder>
            <Text size="sm" c="dimmed">{str("RETELL_KB_AUDIT.TOTAL_KBS")}</Text>
            <Title order={2}>{total}</Title>
          </Paper>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Paper p="md" withBorder>
            <Text size="sm" c="dimmed">{str("RETELL_KB_AUDIT.ORPHANS")}</Text>
            <Title order={2}>{orphans}</Title>
          </Paper>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Paper p="md" withBorder>
            <Text size="sm" c="dimmed">{str("RETELL_KB_AUDIT.LINKED")}</Text>
            <Title order={2}>{linked}</Title>
          </Paper>
        </Grid.Col>
      </Grid>

      <Paper shadow="xs" p="md">
        {errorMessage && (
          <Alert color="red" mb="md" title={str("RETELL_KB_AUDIT.LOAD_ERROR_TITLE")}>
            <Stack gap="xs">
              <Text size="sm">{errorMessage}</Text>
              <Group>
                <Button variant="outline" onClick={loadData} leftSection={<FaSync />}>
                  {str("RETELL_KB_AUDIT.RETRY")}
                </Button>
              </Group>
            </Stack>
          </Alert>
        )}

        <Group justify="space-between" mb="md" align="end">
          <SegmentedControl
            value={filter}
            onChange={(value) => setFilter(value as FilterKey)}
            data={[
              { label: str("ALL"), value: "all" },
              { label: str("RETELL_KB_AUDIT.ORPHANS"), value: "orphans" },
              { label: str("RETELL_KB_AUDIT.LINKED"), value: "linked" },
            ]}
          />
          <TextInput
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            placeholder={str("RETELL_KB_AUDIT.SEARCH_PLACEHOLDER")}
            leftSection={<FaSearch size={14} />}
            w={320}
          />
        </Group>

        <Table.ScrollContainer minWidth={1150}>
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{str("RETELL_KB_AUDIT.KNOWLEDGE_BASE_NAME")}</Table.Th>
                <Table.Th>{str("RETELL_KB_AUDIT.KNOWLEDGE_BASE_ID")}</Table.Th>
                <Table.Th>{str("RETELL_KB_AUDIT.RETELL_STATUS")}</Table.Th>
                <Table.Th>{str("RETELL_KB_AUDIT.IN_DATABASE")}</Table.Th>
                <Table.Th>{str("RETELL_KB_AUDIT.CATEGORY")}</Table.Th>
                <Table.Th>{str("RETELL_KB_AUDIT.COMPANY")}</Table.Th>
                <Table.Th>{str("RETELL_KB_AUDIT.NEXT_PAYMENT")}</Table.Th>
                <Table.Th>{str("RETELL_KB_AUDIT.COMPANY_ACTIVE")}</Table.Th>
                <Table.Th>{str("ACTIONS")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filteredRows.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={9}>
                    <Text c="dimmed" ta="center" py="md">
                      {str("RETELL_KB_AUDIT.EMPTY")}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}

              {filteredRows.map((row) => (
                <Table.Tr key={row.knowledge_base_id}>
                  <Table.Td>{row.knowledge_base_name || "-"}</Table.Td>
                  <Table.Td>
                    <Text ff="monospace" size="sm">{row.knowledge_base_id}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={statusColorMap[row.status] || "gray"} variant="light">
                      {row.status || "-"}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {row.in_database
                      ? <Badge color="green" variant="light">{str("RETELL_KB_AUDIT.YES")}</Badge>
                      : <Badge color="orange" variant="light">{str("RETELL_KB_AUDIT.NO")}</Badge>}
                  </Table.Td>
                  <Table.Td>{row.category?.title || "-"}</Table.Td>
                  <Table.Td>{row.company?.name || str("RETELL_KB_AUDIT.NOT_LINKED")}</Table.Td>
                  <Table.Td>{formatDate(row.company?.next_payment)}</Table.Td>
                  <Table.Td>{getCompanyActiveBadge(row)}</Table.Td>
                  <Table.Td>
                    {row.in_database ? (
                      <Tooltip label={str("RETELL_KB_AUDIT.MANAGED_BY_DB_FLOW")}> 
                        <div>
                          <Button size="xs" variant="subtle" disabled>
                            {str("RETELL_KB_AUDIT.DELETE_IN_RETELL")}
                          </Button>
                        </div>
                      </Tooltip>
                    ) : (
                      <Button
                        size="xs"
                        color="red"
                        variant="light"
                        leftSection={<FaTrash />}
                        onClick={() => setConfirmDelete(row)}
                        loading={deletingId === row.knowledge_base_id}
                        disabled={!!deletingId && deletingId !== row.knowledge_base_id}
                      >
                        {str("RETELL_KB_AUDIT.DELETE_IN_RETELL")}
                      </Button>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>
    </Box>
  );
}
